import {
  PurchasedGoodsFoodListParams,
  PurchasedGoodsFoodPreviewRetrieveParams,
} from '@shared/api/types';
import { parseAsArrayOf, parseAsString } from 'nuqs';

const arrayOfStrings = parseAsArrayOf(parseAsString);

export enum FoodFilterKey {
  Site = 'sites',
  Period = 'timePeriods',
  Supplier = 'suppliers',
  ActivitiesImport = 'activitiesImport',
  Tag = 'tags',
  Created = 'created',
  Category = 'category',
}

export enum FoodFilterColumn {
  Site = 'site',
  Period = 'period',
  Supplier = 'supplier',
  ActivitiesImport = 'activitiesImport',
  Tag = 'tag',
  RemoteId = 'remoteId',
  Created = 'created',
  Category = 'category',
}

export enum FoodFilterApiKey {
  Site = 'sites',
  Period = 'time_periods',
  Supplier = 'suppliers',
  ActivitiesImport = 'activity_imports',
  Tag = 'tags',
  Created = 'created',
  Category = 'category',
}

export const foodFilters = {
  [FoodFilterKey.Site]: {
    column: FoodFilterColumn.Site,
    apiKey: FoodFilterApiKey.Site,
    type: arrayOfStrings,
  },
  [FoodFilterKey.Period]: {
    column: FoodFilterColumn.Period,
    apiKey: FoodFilterApiKey.Period,
    type: arrayOfStrings,
  },
  [FoodFilterKey.Supplier]: {
    column: FoodFilterColumn.Supplier,
    apiKey: FoodFilterApiKey.Supplier,
    type: arrayOfStrings,
  },
  [FoodFilterKey.ActivitiesImport]: {
    column: FoodFilterColumn.ActivitiesImport,
    apiKey: FoodFilterApiKey.ActivitiesImport,
    type: arrayOfStrings,
  },
  [FoodFilterKey.Tag]: {
    column: FoodFilterColumn.Tag,
    apiKey: FoodFilterApiKey.Tag,
    type: arrayOfStrings,
  },
  [FoodFilterKey.Created]: {
    column: FoodFilterColumn.Created,
    apiKey: FoodFilterApiKey.Created,
    type: arrayOfStrings,
  },
  // One path down the product taxonomy, outermost first; it is spread over
  // the three tier params rather than sent under its own key.
  [FoodFilterKey.Category]: {
    column: FoodFilterColumn.Category,
    apiKey: FoodFilterApiKey.Category,
    type: arrayOfStrings,
  },
};

export type FoodFilterOption = keyof typeof foodFilters;

export const foodFilterKeys = Object.keys(foodFilters) as FoodFilterOption[];

/** The list and preview params a taxonomy path selects, one slug per tier. */
export const taxonomyPathParams = (
  path: readonly string[] | null | undefined
): Pick<
  PurchasedGoodsFoodListParams,
  'product_categories' | 'product_sub_categories' | 'product_bases'
> => ({
  ...(path?.[0] ? { product_categories: [path[0]] } : {}),
  ...(path?.[1] ? { product_sub_categories: [path[1]] } : {}),
  ...(path?.[2] ? { product_bases: [path[2]] } : {}),
});

type FoodFilterValues = Partial<
  Record<FoodFilterColumn, string[] | null | undefined>
>;

type FoodFilterParams = PurchasedGoodsFoodListParams &
  PurchasedGoodsFoodPreviewRetrieveParams;

/**
 * The filter part of a list or preview request from the table's URL state,
 * keyed by column. Every filter maps straight onto its API key except the
 * category path, which fans out across the taxonomy tier params.
 */
export const buildFoodFilterParams = (
  values: FoodFilterValues
): FoodFilterParams => {
  const direct: Partial<Record<FoodFilterApiKey, string[] | undefined>> = {};
  Object.values(foodFilters).forEach(({ apiKey, column }) => {
    if (column === FoodFilterColumn.Category) return;
    direct[apiKey] = values[column] || undefined;
  });
  return {
    ...(direct as FoodFilterParams),
    ...taxonomyPathParams(values[FoodFilterColumn.Category]),
  };
};
